import { useGetMeetingErrorDetails, useGetMeetingIssues } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Modal from '@components/V4/Modal/Modal';
import Select from '@components/V4/Select/Select';
import {
  ExclamationCircleIcon,
  ExclamationIcon,
  InformationCircleIcon,
  VideoCameraIcon,
} from '@heroicons/react/outline';
import {
  EmptyState,
  FilterField,
  MetricCard,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import DetailsModal from '@modules/Admin/components/DetailsModal/DetailsModal';
import useDetailsModal from '@modules/Admin/hooks/useDetailsModal';
import {
  MeetingIssueRowDTO,
  MeetingIssueSeverity,
  MeetingIssueType,
} from '@shared/apis/meetingIssues.apis';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useMemo, useState } from 'react';

const PAGE_SIZE = 50;

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 align-top transition-colors last:border-b-0';
const leftHead = 'px-5 py-3 text-left font-semibold';
const leftCell = 'px-5 py-3 text-left text-customLight';

const SEVERITY_STYLES: Record<MeetingIssueSeverity, string> = {
  critical: 'bg-red-500/15 text-red-300',
  warning: 'bg-amber-500/15 text-amber-300',
  info: 'bg-white/10 text-customGrey',
};

const DAY_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 60 days', value: 60 },
  { label: 'Last 120 days', value: 120 },
];

const SEVERITY_OPTIONS = [
  { label: 'Critical only', value: 'critical' },
  { label: 'Critical and warnings', value: 'warning' },
  { label: 'Everything', value: 'info' },
];

const SEVERITY_RANK: Record<MeetingIssueSeverity, number> = { critical: 2, warning: 1, info: 0 };

const ISSUE_OPTIONS: { label: string; value: MeetingIssueType }[] = [
  { label: 'Attendance not recorded', value: 'no_attendance_recorded' },
  { label: 'Nothing recorded', value: 'nothing_recorded' },
  { label: 'Transcription stuck', value: 'transcription_stuck' },
  { label: 'Recording without transcript', value: 'no_transcript' },
  { label: 'AI feedback failed', value: 'ai_feedback_failed' },
  { label: 'AI feedback missing', value: 'ai_feedback_missing' },
  { label: 'Not linked to a session', value: 'not_linked_to_session' },
  { label: 'Zoom webhook failed', value: 'webhook_failures' },
  { label: 'Video errors in browser', value: 'client_errors' },
  { label: 'Participant audio without speech', value: 'silent_participant_audio' },
  { label: 'Recorder problem', value: 'recorder_problem' },
];

const worstSeverity = (row: MeetingIssueRowDTO) =>
  row.issues.reduce<MeetingIssueSeverity>(
    (worst, issue) =>
      SEVERITY_RANK[issue.severity] > SEVERITY_RANK[worst] ? issue.severity : worst,
    'info'
  );

const ERROR_LOG_TYPES: MeetingIssueType[] = ['client_errors', 'webhook_failures'];

const ErrorText = ({ text }: { text?: string }) =>
  text ? (
    <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-black/30 p-3 text-xs text-customGrey">
      {text}
    </pre>
  ) : null;

const MeetingErrorsModal = (props: { row?: MeetingIssueRowDTO; onClose: () => void }) => {
  const { row, onClose } = props;
  const { data, isLoading } = useGetMeetingErrorDetails(row?.meetingId);

  return (
    <Modal isOpen={!!row} onClose={onClose} size="large">
      <Modal.Header
        title={
          row ? `${row.title} · ${moment(row.scheduledDate).format('DD MMM YYYY, HH:mm')}` : ''
        }
      />
      <Modal.Body className="flex flex-col gap-6 text-sm text-white">
        {isLoading || !data ? (
          <div className="flex h-48 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <section className="flex flex-col gap-3">
              <SectionHeading
                title={`Zoom webhook failures (${data.failedEvents.length})`}
                description="Steps of the webhook processor that failed for this meeting: recording upload, transcript, chat or AI feedback."
              />
              {!data.failedEvents.length ? (
                <p className="text-customGrey">None.</p>
              ) : (
                data.failedEvents.map((event) => (
                  <div key={event._id} className="rounded-xl bg-surfaceHover p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-red-500/15 px-2.5 py-0.5 text-xs font-semibold text-red-300">
                        {event.eventType}
                      </span>
                      <span className="text-xs text-customGrey">
                        {moment(event.at).format('DD MMM YYYY, HH:mm')} · {event.env} ·{' '}
                        {event.retryCount} retr{event.retryCount === 1 ? 'y' : 'ies'}
                      </span>
                    </div>
                    {event.errors.length ? (
                      event.errors.map((error, index) => <ErrorText key={index} text={error} />)
                    ) : (
                      <p className="mt-2 text-xs text-customGrey">
                        No error message was stored for this failure. Failures recorded from now on
                        keep the message and stack trace.
                      </p>
                    )}
                  </div>
                ))
              )}
            </section>

            <section className="flex flex-col gap-3">
              <SectionHeading
                title={`Video errors in the browser (${data.clientErrors.length})`}
                description="Errors the Zoom Video SDK reported from participants' browsers during the meeting."
              />
              {!data.clientErrors.length ? (
                <p className="text-customGrey">None.</p>
              ) : (
                data.clientErrors.map((error) => (
                  <div key={error._id} className="rounded-xl bg-surfaceHover p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-semibold text-amber-300">
                        {error.errorType || 'Error'}
                      </span>
                      <span className="text-xs text-customGrey">
                        {moment(error.at).format('DD MMM YYYY, HH:mm:ss')}
                        {error.username ? ` · ${error.username}` : ''}
                      </span>
                    </div>
                    {error.errorMessage ? (
                      <p className="mt-2 break-words text-customLight">{error.errorMessage}</p>
                    ) : null}
                    {error.stackTrace ? (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-xs text-customGrey">
                          Stack trace
                        </summary>
                        <ErrorText text={error.stackTrace} />
                      </details>
                    ) : null}
                  </div>
                ))
              )}
            </section>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

const MeetingIssues = () => {
  const [days, setDays] = useState(30);
  const [minimumSeverity, setMinimumSeverity] = useState<MeetingIssueSeverity>('warning');
  const [issueTypes, setIssueTypes] = useState<MeetingIssueType[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [errorsRow, setErrorsRow] = useState<MeetingIssueRowDTO>();

  const { data, isLoading, isFetching } = useGetMeetingIssues({ days }, { keepPreviousData: true });

  const {
    activeModalView,
    isModalOpen,
    mentorId,
    studentId,
    sessionId,
    enrollmentId,
    openModal,
    closeModal,
  } = useDetailsModal({ page: 'session', tab: 'meetings' });

  const rows = useMemo(() => data?.meetings ?? [], [data]);

  const filtered = useMemo(
    () =>
      rows
        .map((row) => ({
          ...row,
          hasErrorLogs: row.issues.some((issue) => ERROR_LOG_TYPES.includes(issue.type)),
          issues: row.issues.filter(
            (issue) =>
              SEVERITY_RANK[issue.severity] >= SEVERITY_RANK[minimumSeverity] &&
              (!issueTypes.length || issueTypes.includes(issue.type))
          ),
        }))
        .filter((row) => row.issues.length),
    [rows, minimumSeverity, issueTypes]
  );

  const counts = useMemo(() => {
    const bySeverity = { critical: 0, warning: 0, info: 0 };
    rows.forEach((row) => (bySeverity[worstSeverity(row)] += 1));
    return bySeverity;
  }, [rows]);

  const typeCounts = useMemo(() => {
    const result = new Map<MeetingIssueType, number>();
    rows.forEach((row) =>
      row.issues.forEach((issue) => result.set(issue.type, (result.get(issue.type) ?? 0) + 1))
    );
    return result;
  }, [rows]);

  if (isLoading || !data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const resetPaging = () => setVisibleCount(PAGE_SIZE);

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Meeting Issues</h1>
      </div>

      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 laptop:grid-cols-4">
        <MetricCard
          label="Ended meetings checked"
          value={data.scanned}
          icon={<VideoCameraIcon className="h-5 w-5" />}
          accent="teal"
        />
        <MetricCard
          label="With a critical issue"
          value={counts.critical}
          icon={<ExclamationCircleIcon className="h-5 w-5" />}
          accent="lila"
          onClick={() => {
            setMinimumSeverity('critical');
            resetPaging();
          }}
        />
        <MetricCard
          label="Worst issue is a warning"
          value={counts.warning}
          icon={<ExclamationIcon className="h-5 w-5" />}
          accent="gold"
          onClick={() => {
            setMinimumSeverity('warning');
            resetPaging();
          }}
        />
        <MetricCard
          label="Only informational"
          value={counts.info}
          icon={<InformationCircleIcon className="h-5 w-5" />}
          accent="green"
          onClick={() => {
            setMinimumSeverity('info');
            resetPaging();
          }}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterField width="tablet:w-44">
          <Select
            variant="secondary"
            label="Period"
            allowClear={false}
            value={days}
            options={DAY_OPTIONS}
            onChange={(value) => {
              setDays(Number(value) || 30);
              resetPaging();
            }}
          />
        </FilterField>
        <FilterField width="tablet:w-56">
          <Select
            variant="secondary"
            label="Severity"
            allowClear={false}
            value={minimumSeverity}
            options={SEVERITY_OPTIONS}
            onChange={(value) => {
              setMinimumSeverity(value as MeetingIssueSeverity);
              resetPaging();
            }}
          />
        </FilterField>
        <FilterField width="tablet:w-80">
          <Select
            variant="secondary"
            label="Issue"
            mode="multiple"
            placeholder="Any issue"
            value={issueTypes}
            options={ISSUE_OPTIONS.map((option) => ({
              ...option,
              label: `${option.label} (${typeCounts.get(option.value) ?? 0})`,
            }))}
            onChange={(value) => {
              setIssueTypes((value as MeetingIssueType[]) ?? []);
              resetPaging();
            }}
          />
        </FilterField>
      </div>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title={`Meetings (${filtered.length})`}
          description="AI feedback is only generated from Zoom cloud transcripts, so meetings recorded only by the new recorder are not reported as missing AI feedback."
        />
        {!filtered.length ? (
          <EmptyState>No meetings with issues for these filters.</EmptyState>
        ) : (
          <div className={mergeClassNames('flex flex-col gap-4', isFetching ? 'opacity-60' : '')}>
            <TableShell minWidth="min-w-[1100px]">
              <thead>
                <tr className={headRow}>
                  <th className={leftHead}>Date</th>
                  <th className={leftHead}>Meeting</th>
                  <th className={leftHead}>Mentor</th>
                  <th className={leftHead}>Students</th>
                  <th className={leftHead}>Issues</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, visibleCount).map((row) => {
                  const canOpen = !!row.sessionId;
                  const open = () =>
                    row.sessionId &&
                    openModal({ page: 'session', tab: 'meetings' }, { sessionId: row.sessionId });

                  return (
                    <tr
                      key={row.meetingId}
                      className={mergeClassNames(
                        bodyRow,
                        canOpen ? 'cursor-pointer hover:bg-surfaceHover' : ''
                      )}
                      tabIndex={canOpen ? 0 : undefined}
                      onClick={open}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') open();
                      }}
                    >
                      <td className={mergeClassNames(leftCell, 'whitespace-nowrap')}>
                        {moment(row.scheduledDate).format('DD MMM YYYY, HH:mm')}
                        {row.durationMinutes !== undefined ? (
                          <div className="text-xs text-customGrey">{row.durationMinutes} min</div>
                        ) : null}
                      </td>
                      <td className={leftCell}>{row.title}</td>
                      <td className={leftCell}>{row.mentor?.name ?? '—'}</td>
                      <td className={mergeClassNames(leftCell, 'text-xs')}>
                        {row.students.map((student) => student.name).join(', ') || '—'}
                      </td>
                      <td className="px-5 py-3">
                        <ul className="flex flex-col gap-1">
                          {row.issues.map((issue) => (
                            <li key={issue.type} className="flex flex-wrap items-center gap-2">
                              <span
                                className={mergeClassNames(
                                  'whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold',
                                  SEVERITY_STYLES[issue.severity]
                                )}
                              >
                                {issue.label}
                              </span>
                              {issue.detail ? (
                                <span className="text-xs text-customGrey">{issue.detail}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                        {row.hasErrorLogs ? (
                          <button
                            type="button"
                            className="mt-2 text-xs font-semibold text-customPrimary hover:underline"
                            onClick={(event) => {
                              event.stopPropagation();
                              setErrorsRow(row);
                            }}
                          >
                            View errors
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </TableShell>
            {filtered.length > visibleCount ? (
              <button
                type="button"
                className="self-start text-sm text-customPrimary"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              >
                Show more ({filtered.length - visibleCount} left)
              </button>
            ) : null}
          </div>
        )}
      </section>

      <MeetingErrorsModal row={errorsRow} onClose={() => setErrorsRow(undefined)} />

      {isModalOpen && (
        <DetailsModal
          {...activeModalView}
          mentorId={mentorId}
          studentId={studentId}
          enrollmentId={enrollmentId}
          sessionId={sessionId}
          isOpen={true}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default MeetingIssues;
